import {ReactElement, useCallback, useEffect, useRef} from 'react';
import {getKeyboardKeyType} from './getKeyboardKeyType';
import {KeyboardKeyType} from './KeyboardKeyType.enum';

interface SmoothKeyScrollerProps {
  children: ReactElement;
  enableScroll?: boolean;
  scrollSpeed?: number;
}

export default function SmoothKeyScroller({
  children,
  enableScroll = true,
  scrollSpeed = 25,
}: SmoothKeyScrollerProps) {
  const scrollDirection = useRef<Direction>(Direction.None);
  const scrollDistance = useRef<number>(0);
  const currentFrame = useRef<number | null>(null);

  const step = useCallback(() => {
    currentFrame.current = window.requestAnimationFrame(step);
    window.scrollBy(0, scrollDistance.current);
  }, []);

  const scroll = useCallback(
    (direction: Direction) => {
      scrollDirection.current = direction;
      scrollDistance.current =
        scrollDirection.current === Direction.Up ? -scrollSpeed : scrollSpeed;
      if (!currentFrame.current) {
        currentFrame.current = window.requestAnimationFrame(step);
      }
    },
    [scrollSpeed, step]
  );

  const stop = useCallback(() => {
    scrollDirection.current = Direction.None;
    scrollDistance.current = 0;
    if (currentFrame.current) {
      window.cancelAnimationFrame(currentFrame.current);
      currentFrame.current = null;
    }
  }, []);

  const onKeyEvent = useCallback(
    (event: KeyboardEvent) => {
      const modifier =
        event.ctrlKey || event.altKey || event.metaKey || event.shiftKey;
      const keyType = getKeyboardKeyType(event);
      if (
        event.defaultPrevented ||
        modifier ||
        (keyType !== KeyboardKeyType.ArrowUp &&
          keyType !== KeyboardKeyType.ArrowDown)
      ) {
        return;
      }
      const newDirection =
        keyType === KeyboardKeyType.ArrowUp ? Direction.Up : Direction.Down;
      if (event.type === 'keydown') {
        // 호출하지 않으면 브라우저의 기본 스크롤 동작 때문에 끊기는 현상이 생긴다.
        event.preventDefault();
        scroll(newDirection);
      } else if (
        event.type === 'keyup' &&
        newDirection === scrollDirection.current
      ) {
        stop();
      }
    },
    [scroll, stop]
  );

  useEffect(() => {
    removeEventListener('keydown', onKeyEvent);
    removeEventListener('keyup', onKeyEvent);
    if (enableScroll) {
      addEventListener('keydown', onKeyEvent);
      addEventListener('keyup', onKeyEvent);
    }
    return () => {
      stop();
      removeEventListener('keydown', onKeyEvent);
      removeEventListener('keyup', onKeyEvent);
    };
  }, [enableScroll, onKeyEvent, stop]);

  return children;
}

enum Direction {
  None = 0,
  Up = -1,
  Down = 1,
}
